import React, { useEffect } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Routes, Route } from "react-router-dom";

import { appRoutes } from "./routes/routes";

import SplashScreen from "./screens/onboarding/splash/SplashScreen";
import LoginScreen from "./screens/onboarding/login/LoginScreen";
import { ToastContainer } from "react-toastify";
import HomeScreen from "./screens/home/HomeScreen";
import SettingsScreen from "./screens/settings/settings/SettingsScreen";
import ChatsScreen from "./screens/chats/chats/ChatsScreen";
import NoticesListScreen from "./screens/notices/notices_list/NoticesListScreen";
import SearchScreen from "./screens/search/SearchScreen";
import NotFoundScreen from "./screens/not_found/NotFoundScreen";
import Loader from "./widget/common/ui/Loader";
import {
  MiddlewareDashboard,
  MiddlewareLogin,
} from "./routes/routesMiddleware";
import Modals from "./widget/common/ui/Modals";
import ProductsScreen from "./screens/products/ProductsScreen";
import EmailScreen from "./screens/email/EmailScreen";
import BranchDeptChatsScreen from "./screens/branch/branch_dept_chats/BranchDeptChatsScreen";
import BranchGroupsScreen from "./screens/branch/branch_groups/BranchGroupsScreen";
import BranchTeamScreen from "./screens/branch/branch_team/BranchTeamScreen";
import SettingsChangeDefaultBranchScreen from "./screens/settings/change_default_branch/SettingsChangeDefaultBranchScreen";
import BranchFollowGroupScreen from "./screens/branch/branch_follow_groups/BranchFollowGroupsScreen";
import NoticesDetailScreen from "./screens/notices/notices_detail/NoticesDetailScreen";
import FollowingListScreen from "./screens/notices/following_list/FollowingListScreen";
import ImageDocumentPreviewScreen from "./screens/preview/image_document_preview/ImageDocumentPreviewScreen";
import SettingsPersonalInfoScreen from "./screens/settings/manage_personal_profile/SettingsPersonalInfoScreen";
import DeactivateAccountReasonScreen from "./screens/deactivate_account/deactivate_account_reason/DeactivateAccountReasonScreen";
import DeactivateAccountScreen from "./screens/deactivate_account/deactivate_account_password_alert/DeactivateAccountScreen";
import SettingsPersonalSecurityScreen from "./screens/settings/security/personal_security/SettingsPersonalSecurityScreen";
import NewPasswordScreen from "./screens/onboarding/forgot_password/new_password/NewPasswordScreen";
import RecoverPasswordScreen from "./screens/onboarding/forgot_password/recover_password/RecoverPasswordScreen";
import SettingsAccountVericationScreen from "./screens/settings/security/account_verification/SettingsAccountVericationScreen";
import SignupThirdPartyScreen from "./screens/onboarding/signup/third_party_selection/SignupThirdPartyScreen";
import SignupPersonalInformationScreen from "./screens/onboarding/signup/personal_information/SignupPersonalInformationScreen";
import SignupAccountVerificationScreen from "./screens/onboarding/signup/account_verification/SignupAccountVerificationScreen";
import SignupAccountVerificationChangeScreen from "./screens/onboarding/signup/account_verification_change/SignupAccountVerificationChangeScreen";
import SignupPersonalLogoUploadScreen from "./screens/onboarding/signup/personal_logo_upload/SignupPersonalLogoUploadScreen";
import NavigationInitializer from "./service/navigation/NavigationInitializer";
import HomeGuestUserScreen from "./screens/home_guest_user/guest_home/HomeGuestUserScreen";
import GuestUserFormScreen from "./screens/home_guest_user/guest_user_form/GuestUserFormScreen";
import StorageKeys from "./const/StorageKeys";
import CacheStorage from "./service/storage/CacheStorage";
import UtilityHelper from "./service/utilities/UtilityHelper";
import { getFCMToken } from "./service/firebase/Firebase";
import BoardingScreen from "./screens/onboarding/boarding/BoardingScreen";
import TeamChatScreen from "./screens/chats/team_chat/TeamChatScreen";
import DepartmentChatScreen from "./screens/chats/department_chat/DepartmentChatScreen";
import GroupChatScreen from "./screens/chats/group_chat/GroupChatScreen";
import BranchSwitchSplashScreen from "./screens/branch/branch_switch_flash/BranchSwitchSplashScreen";
import { useSelector } from "react-redux";
import { RootState } from "./libraries/state_reducers/store";

function App() {
  const SITE_TITLE = process.env.REACT_APP_site_title;
  // const IS_DEBUG = process.env.NODE_ENV;
  //console.log(IS_DEBUG);

  const siteTitle = useSelector((state: RootState) => state.global.siteTitle);
  const TITLE = UtilityHelper.isNotEmpty(siteTitle ?? "")
    ? " - " + siteTitle
    : "";

  const setupFCM = async () => {
    const deviceToken: string = CacheStorage.getValue(StorageKeys.device_token);
    if (UtilityHelper.isEmpty(deviceToken)) {
      await getFCMToken();
    }
  };

  useEffect(() => {
    setupFCM();
  }, []);

  return (
    <HelmetProvider>
      <Helmet>
        <title>
          {SITE_TITLE}
          {TITLE}
        </title>
      </Helmet>

      {/* ✅ Ensure this is outside Routes and only mounts ONCE */}
      <NavigationInitializer />

      <Routes>
        <Route
          path={appRoutes.routeNameBoarding}
          element={<BoardingScreen />}
        />
        <Route
          path={appRoutes.routeNameBoardingSplash}
          element={<SplashScreen />}
        />
        <Route path={appRoutes.routeNameSplash} element={<SplashScreen />} />

        <Route
          path={appRoutes.routeNameLogin}
          element={
            <MiddlewareLogin>
              <LoginScreen />
            </MiddlewareLogin>
          }
        />
        <Route
          path={appRoutes.routeNameNewPassword}
          element={
            <MiddlewareLogin>
              <NewPasswordScreen />
            </MiddlewareLogin>
          }
        />
        <Route
          path={appRoutes.routeNameRecoverPassword}
          element={
            <MiddlewareLogin>
              <RecoverPasswordScreen />
            </MiddlewareLogin>
          }
        />

        <Route
          path={appRoutes.routeNameSignupThirdParty}
          element={
            <MiddlewareLogin>
              <SignupThirdPartyScreen />
            </MiddlewareLogin>
          }
        />
        <Route
          path={appRoutes.routeNameSignupPersonalInfo}
          element={
            <MiddlewareLogin>
              <SignupPersonalInformationScreen />
            </MiddlewareLogin>
          }
        />
        <Route
          path={appRoutes.routeNameSignupVerification}
          element={
            <MiddlewareLogin>
              <SignupAccountVerificationScreen />
            </MiddlewareLogin>
          }
        />
        <Route
          path={appRoutes.routeNameForgotPasswordVerification}
          element={
            <MiddlewareLogin>
              <SignupAccountVerificationScreen />
            </MiddlewareLogin>
          }
        />
        <Route
          path={appRoutes.routeNameSignupVerificationChange}
          element={
            <MiddlewareLogin>
              <SignupAccountVerificationChangeScreen />
            </MiddlewareLogin>
          }
        />
        <Route
          path={appRoutes.routeNameSignupUploadLogo}
          element={<SignupPersonalLogoUploadScreen />}
        />

        <Route
          path={appRoutes.routeNameHome}
          element={
            <MiddlewareDashboard>
              <HomeScreen />
            </MiddlewareDashboard>
          }
        />

        <Route
          path={appRoutes.routeNameHomeGuest}
          element={
            <MiddlewareDashboard allowGuest={true}>
              <HomeGuestUserScreen />
            </MiddlewareDashboard>
          }
        />

        <Route
          path={appRoutes.routeNameBranchSwitchSplash}
          element={<BranchSwitchSplashScreen />}
        />

        <Route
          path={appRoutes.routeNameBranchFollowGroup}
          element={
            <MiddlewareDashboard>
              <BranchFollowGroupScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameSearch}
          element={
            <MiddlewareDashboard>
              <SearchScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameChats}
          element={
            <MiddlewareDashboard>
              <ChatsScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameNotices}
          element={
            <MiddlewareDashboard>
              <NoticesListScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameNoticesDetail}
          element={
            <MiddlewareDashboard>
              <NoticesDetailScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameNoticesFollowing}
          element={
            <MiddlewareDashboard>
              <FollowingListScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameEmail}
          element={
            <MiddlewareDashboard allowGuest={true}>
              <EmailScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameProducts}
          element={
            <MiddlewareDashboard allowGuest={true}>
              <ProductsScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameBranchDeptChats}
          element={
            <MiddlewareDashboard>
              <BranchDeptChatsScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameBranchGroups}
          element={
            <MiddlewareDashboard>
              <BranchGroupsScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameBranchTeam}
          element={
            <MiddlewareDashboard>
              <BranchTeamScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameSettings}
          element={
            <MiddlewareDashboard>
              <SettingsScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameSettingsChangeDefaultBranch}
          element={
            <MiddlewareDashboard>
              <SettingsChangeDefaultBranchScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameSettingsPersonalSecurityEmail}
          element={
            <MiddlewareDashboard>
              <SettingsPersonalSecurityScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameSettingsPersonalSecurityPassword}
          element={
            <MiddlewareDashboard>
              <SettingsPersonalSecurityScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameSettingsPersonalSecurityPhone}
          element={
            <MiddlewareDashboard>
              <SettingsPersonalSecurityScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameSettingsAccountVerification}
          element={
            <MiddlewareDashboard>
              <SettingsAccountVericationScreen />
            </MiddlewareDashboard>
          }
        />

        <Route
          path={appRoutes.routeNameSettingsDeactivateAccountAlert}
          element={
            <MiddlewareDashboard>
              <DeactivateAccountScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameSettingsDeactivateAccountReason}
          element={
            <MiddlewareDashboard>
              <DeactivateAccountReasonScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameSettingsPeronalInfo}
          element={
            <MiddlewareDashboard>
              <SettingsPersonalInfoScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNamePreviewImageDocs}
          element={
            <MiddlewareDashboard>
              <ImageDocumentPreviewScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameGuestFormGeneral}
          element={
            <MiddlewareDashboard allowGuest={true}>
              <GuestUserFormScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameGuestFormFeedback}
          element={
            <MiddlewareDashboard allowGuest={true}>
              <GuestUserFormScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameChatTeam}
          element={
            <MiddlewareDashboard>
              <TeamChatScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameChatDepartment}
          element={
            <MiddlewareDashboard>
              <DepartmentChatScreen />
            </MiddlewareDashboard>
          }
        />
        <Route
          path={appRoutes.routeNameChatGroup}
          element={
            <MiddlewareDashboard>
              <GroupChatScreen />
            </MiddlewareDashboard>
          }
        />
        {/* Catch-all route */}
        <Route path="*" element={<NotFoundScreen />} />
      </Routes>
      <Modals />
      <ToastContainer />
      {Loader.modalHtml()}
    </HelmetProvider>
  );
}

export default App;
